import React from "react";
import { Table } from "antd";
import { ColumnType } from "antd/lib/table";
import { isNil } from "lodash";
import { EComModal } from "components/EComModal";
import { AiccPromptModel } from "types/model/aiccPrompt";
import {
  COST_CACHE_WRITE_AS_INPUT,
  COST_NOT_PRICED,
  COST_UNKNOWN,
  EFFORT_LABEL,
  MODEL_COST_DEFAULT,
  MODEL_COST_DESCRIPTION,
  MODEL_COST_TITLE,
} from "../configs";
import styles from "./ModelSettings.module.scss";

interface Props {
  visible: boolean;
  models: AiccPromptModel[];
  onClose: () => void;
}

const price = (amount: number | undefined, { isPriced, currency }: AiccPromptModel) => {
  if (!isPriced) {
    return COST_NOT_PRICED;
  }
  if (isNil(amount)) {
    return COST_UNKNOWN;
  }

  return currency ? `${currency} ${amount}` : `${amount}`;
};

const COLUMNS: ColumnType<AiccPromptModel>[] = [
  {
    title: "Model",
    dataIndex: "name",
    key: "name",
    render: (_, { name, isDefault, efforts }) => {
      const notes = [
        isDefault ? MODEL_COST_DEFAULT : "",
        efforts.length ? `${EFFORT_LABEL}: ${efforts.join(" / ")}` : "",
      ]
        .filter(Boolean)
        .join(" · ");

      return (
        <div>
          <div>{name}</div>
          {!!notes && <div className={styles.costDescription}>{notes}</div>}
        </div>
      );
    },
  },
  {
    title: "Input / 1M",
    dataIndex: "inputPerMillion",
    key: "inputPerMillion",
    width: 130,
    className: styles.costCell,
    render: (_, model) => price(model.inputPerMillion, model),
  },
  {
    title: "Cached input / 1M",
    dataIndex: "cachedInputPerMillion",
    key: "cachedInputPerMillion",
    width: 150,
    className: styles.costCell,
    render: (_, model) => price(model.cachedInputPerMillion, model),
  },
  {
    title: "Cache write / 1M",
    dataIndex: "cacheWritePerMillion",
    key: "cacheWritePerMillion",
    width: 150,
    className: styles.costCell,
    render: (_, model) =>
      model.isPriced && isNil(model.cacheWritePerMillion)
        ? COST_CACHE_WRITE_AS_INPUT
        : price(model.cacheWritePerMillion, model),
  },
  {
    title: "Output / 1M",
    dataIndex: "outputPerMillion",
    key: "outputPerMillion",
    width: 130,
    className: styles.costCell,
    render: (_, model) => price(model.outputPerMillion, model),
  },
];

function ModelCostModal({ visible, models, onClose }: Props) {
  return (
    <EComModal visible={visible} title={MODEL_COST_TITLE} onCancel={onClose} footer={null} width={900}>
      <div className={styles.panelDescription}>{MODEL_COST_DESCRIPTION}</div>
      <Table<AiccPromptModel>
        columns={COLUMNS}
        dataSource={models}
        rowKey={(record) => record.id}
        pagination={false}
        size="small"
      />
    </EComModal>
  );
}

export default ModelCostModal;
